import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { ProjectSummary } from '@/application/library/listProjects'

export function ProjectCard({ summary }: { summary: ProjectSummary }) {
  const { t } = useTranslation()
  const { project, progress, totalBlocks, reviewedBlocks } = summary
  const pct = Math.round(progress * 100)

  return (
    <Link
      to={`/p/${project.id}`}
      className="rounded border p-4 transition no-underline"
      style={{
        borderColor: 'var(--color-border)',
        color: 'var(--color-fg)',
        display: 'block',
      }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3
          className="text-lg font-medium truncate"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {project.name}
        </h3>
        <span className="text-xs" style={{ color: 'var(--color-muted)' }}>
          {formatRelative(project.updatedAt, t)}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-3">
        <div
          className="h-1.5 flex-1 rounded-full overflow-hidden"
          style={{ background: 'var(--color-border)' }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('library.progress')}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: 'var(--color-accent)',
              transition: 'width 200ms ease',
            }}
          />
        </div>
        <span className="text-xs tabular-nums" style={{ color: 'var(--color-muted)' }}>
          {reviewedBlocks}/{totalBlocks}
        </span>
      </div>
    </Link>
  )
}

function formatRelative(iso: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const then = new Date(iso).getTime()
  const now = Date.now()
  const seconds = Math.floor((now - then) / 1000)
  if (seconds < 60) return t('library.justNow')
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return t('library.minutesAgo', { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return t('library.hoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  return t('library.daysAgo', { count: days })
}
